import React from 'react';
import type { ThemeConfig } from '@/lib/theme-utils';

interface LoadingOverlayProps {
  isVisible: boolean;
  message?: string;
  isAbsolute?: boolean; // Whether to use absolute positioning instead of fixed
  theme?: ThemeConfig;
}

/**
 * Animated ring component for merge-cruise theme loading
 */
const AnimatedRing: React.FC<{ color: string; size: number }> = ({ color, size }) => {
  return (
    <svg
      className="absolute animate-spin"
      style={{
        width: size,
        height: size,
        animationDuration: '2s',
      }}
      viewBox="0 0 100 100"
    >
      <circle
        cx="50"
        cy="50"
        r="45"
        fill="none"
        stroke={color}
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray="70 200"
      />
    </svg>
  );
};

/**
 * Coin loading component for merge-cruise theme
 */
const CoinLoader: React.FC<{ coinImage: string; ringColor: string }> = ({ coinImage, ringColor }) => {
  return (
    <div className="relative flex items-center justify-center" style={{ width: 120, height: 120 }}>
      {/* Animated ring around the coin */}
      <AnimatedRing color={ringColor} size={120} />
      
      {/* Coin image */}
      <img
        src={coinImage}
        alt="Loading"
        className="w-20 h-20 object-contain"
        style={{ zIndex: 1 }}
      />
    </div>
  );
};

/**
 * Default spinner component
 */
const DefaultSpinner: React.FC = () => {
  return (
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800"></div>
  );
};

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ 
  isVisible, 
  message = "Loading...",
  isAbsolute = false,
  theme
}) => {
  if (!isVisible) return null;

  const positionClass = isAbsolute ? "absolute" : "fixed";
  const isCoinTheme = theme?.loading?.type === 'coin' && theme.loading.coinImage;

  return (
    <div className={`${positionClass} top-0 left-0 right-0 bottom-0 backdrop-blur-sm flex items-center justify-center z-50 min-h-full w-full`}>
      <div className="flex flex-col items-center space-y-4">
        {/* Loading indicator - coin or spinner based on theme */}
        {isCoinTheme ? (
          <CoinLoader
            coinImage={theme.loading.coinImage!}
            ringColor={theme.loading.ringColor || '#22C55E'}
          />
        ) : (
          <DefaultSpinner />
        )}
        
        {/* Loading Message */}
        <p className="text-gray-800 text-lg font-medium">{message}</p>
      </div>
    </div>
  );
};

export default LoadingOverlay;
